/**
 * Le Kaai — Restaurant reservation with immersive hero (Beach Club pattern).
 *
 * Parcours :
 *   1. Hero image + card horizontale (calendar left, context right)
 *   2. Auto-scroll vers formulaire après sélection date
 *   3. Formulaire inline : nom, prénom, email, téléphone, personnes,
 *      heure au restaurant, heure de bateau
 *   4. POST /api/bookings (offer_type=le_kaai) → redirection paiement
 *
 * Frais facturés = uniquement traversée aller-retour · 10 000 FCFA / personne.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";
import { ArrowRight, Loader2 } from "lucide-react";
import { Calendar } from "../../components/ui/calendar";
import api from "../../lib/api";
import { trackEvent } from "../../lib/tracking";
import { useSiteConfig, sel } from "../../lib/site-config";
import HeroBackground from "../../components/vitrine/HeroBackground";

const HERO_IMAGE_FALLBACK =
  "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=2400&q=85";

const CROSSING_FEE_PER_PERSON = 10000;

const RESTAURANT_TIMES = [
  "12:00", "12:30", "13:00", "13:30", "14:00",
  "19:00", "19:30", "20:00", "20:30", "21:00",
];

// Boat times exposed on the vitrine — resolved from offer.boat_times when
// the offer catalog loads (falls back to weekday defaults meanwhile).
const FALLBACK_BOAT_TIMES = ["10H", "12H", "14H", "16H", "18H", "20H"];

const fmtXOF = (n) => new Intl.NumberFormat("fr-FR").format(n || 0) + " FCFA";

export default function UniversLeKaai() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [offer, setOffer] = useState(null);
  const [menuInfo, setMenuInfo] = useState(null); // { available, url, filename }
  const formRef = useRef(null);

  const [form, setForm] = useState({
    surname: "",
    name: "",
    email: "",
    phone: "",
    guests: 2,
    restaurant_time: "12:30",
    boat_time: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const iso = useMemo(() => (selected ? format(selected, "yyyy-MM-dd") : null), [selected]);
  const weekdayLabel = useMemo(
    () => (selected ? format(selected, "EEEE d MMMM yyyy", { locale: frLocale }) : ""),
    [selected]
  );

  // Load the offer to get its boat_times for the selected date.
  useEffect(() => {
    if (!iso) { setOffer(null); return; }
    api.get(`/offers?date=${iso}`)
      .then((r) => {
        const items = r.data?.items || r.data || [];
        const found = items.find((o) => o.id === "le_kaai") || null;
        setOffer(found);
        // pre-select the first boat time if empty
        if (found && !form.boat_time) {
          const bt = found.boat_times || found.boat_times_weekday || FALLBACK_BOAT_TIMES;
          if (bt.length > 0) setForm((f) => ({ ...f, boat_time: bt[0] }));
        }
      })
      .catch(() => setOffer(null));
  }, [iso]);

  // Load Le Kaai menu PDF availability (public endpoint)
  useEffect(() => {
    api.get("/le-kaai/menu")
      .then((r) => setMenuInfo(r.data))
      .catch(() => setMenuInfo(null));
  }, []);

  // Smooth-scroll to the reservation form once a date is picked.
  useEffect(() => {
    if (!selected || !formRef.current) return;
    const t = setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, [selected]);

  const totalAmount = form.guests * CROSSING_FEE_PER_PERSON;

  const boatOptions = useMemo(() => {
    if (!offer) return FALLBACK_BOAT_TIMES;
    return offer.boat_times || offer.boat_times_weekday || FALLBACK_BOAT_TIMES;
  }, [offer]);

  const canSubmit =
    !!iso && !!form.name && !!form.surname && !!form.email && !!form.phone &&
    form.guests >= 1 && !!form.restaurant_time && !!form.boat_time && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        offer_type: "le_kaai",
        date: iso,
        adults: form.guests,
        children: 0,
        children_paid: 0,
        children_free: 0,
        boat_time: form.boat_time,
        participants: [
          {
            surname: form.surname,
            name: form.name,
            email: form.email,
            phone: form.phone,
            nationality: "CI",
            kind: "adult",
          },
        ],
        special_requests: `Heure souhaitée au restaurant : ${form.restaurant_time}`,
      };
      trackEvent("le_kaai_booking_submit", { date: iso, guests: form.guests });
      const r = await api.post("/bookings", payload);
      const booking = r.data;
      // Route to the payment page for the boat crossing settlement.
      if (booking?.reference_token) {
        navigate(`/pay/${booking.reference_token}`);
      } else {
        setError("Réservation créée mais impossible d'ouvrir le paiement. Vérifiez votre email.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur — merci de réessayer.";
      setError(typeof detail === "string" ? detail : "Erreur — merci de réessayer.");
    } finally {
      setSubmitting(false);
    }
  }

  // iter-63 — CMS-driven hero
  const cfg = useSiteConfig();
  const ph = sel.pageHeader(cfg, "hero_le_kaai");
  const heroImage = ph.image_url || HERO_IMAGE_FALLBACK;
  const heroKicker = ph.kicker || "· Restaurant Le Kaai ·";
  const heroTitle = ph.title || "Choisissez votre date";
  const heroSubtitle = ph.subtitle_long || "Une cuisine d'inspiration africaine contemporaine, servie face à la lagune. Sélectionnez votre date, remplissez le formulaire et réglez uniquement la traversée aller-retour.";
  const heroFooter = ph.footer_note || "Tous les jours · 11 h — 22 h 30";
  const heroVideo = ph.video_url || "";

  return (
    <div className="bg-white text-[#0A0A0A]" data-testid="univers-le-kaai">
      {/* ─── Immersive Hero — image + integrated calendar card (CMS iter-63/64) ─── */}
      <section className="relative w-full min-h-[100vh] overflow-hidden bg-[#0A0A0A]">
        <HeroBackground videoUrl={heroVideo} imageUrl={heroImage} />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/45" />

        <div className="relative z-10 min-h-[100vh] max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-28 flex flex-col items-center justify-center gap-8 md:gap-10">
          {/* MOBILE title */}
          <div className="md:hidden text-center text-white">
            <div className="text-[0.6rem] tracking-[0.55em] uppercase text-white/85 mb-4">
              {heroKicker}
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl leading-[1.05] whitespace-pre-line">
              {heroTitle}
            </h1>
          </div>

          {/* MOBILE single-column calendar */}
          <div className="md:hidden w-full max-w-md bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] p-4 sm:p-6"
               data-testid="lekaai-calendar-mobile-card">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(d) => {
                setSelected(d);
                if (d) trackEvent("lekaai_date_select", { date: format(d, "yyyy-MM-dd") });
              }}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              locale={frLocale}
              className="mx-auto"
              classNames={{
                day_today: "bg-transparent text-[#0A0A0A] hover:bg-[#B8922A]/10",
                day_selected: "bg-[#B8922A] text-white hover:bg-[#B8922A] hover:text-white focus:bg-[#B8922A] focus:text-white",
              }}
              data-testid="lekaai-calendar-mobile"
            />
            {selected && (
              <div className="text-center mt-4 pt-4 border-t border-[#0A0A0A]/10 text-xs tracking-[0.15em] uppercase text-[#0A0A0A]/70">
                <span className="capitalize">{weekdayLabel}</span>
              </div>
            )}
          </div>

          {/* DESKTOP horizontal landscape card */}
          <div className="hidden md:grid w-full max-w-5xl grid-cols-2 bg-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)]"
               data-testid="lekaai-calendar-card-desktop">
            <div className="p-8 lg:p-10 border-r border-[#0A0A0A]/8">
              <Calendar
                mode="single"
                selected={selected}
                onSelect={(d) => {
                  setSelected(d);
                  if (d) trackEvent("lekaai_date_select", { date: format(d, "yyyy-MM-dd") });
                }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                locale={frLocale}
                className="mx-auto scale-[1.08] origin-top"
                classNames={{
                  day_today: "bg-transparent text-[#0A0A0A] hover:bg-[#B8922A]/10",
                  day_selected: "bg-[#B8922A] text-white hover:bg-[#B8922A] hover:text-white focus:bg-[#B8922A] focus:text-white",
                }}
                data-testid="lekaai-calendar"
              />
              {selected && (
                <div className="text-center mt-6 pt-4 border-t border-[#0A0A0A]/10 text-xs tracking-[0.2em] uppercase text-[#0A0A0A]/70">
                  <span className="capitalize">{weekdayLabel}</span>
                </div>
              )}
            </div>
            <div className="p-10 lg:p-14 flex flex-col justify-center text-[#0A0A0A]">
              <div className="text-[0.62rem] tracking-[0.55em] uppercase text-[#B8922A] mb-6">
                {heroKicker}
              </div>
              <h1 className="font-serif font-light text-4xl lg:text-5xl leading-[1.05] mb-8 whitespace-pre-line">
                {heroTitle}
              </h1>
              <p className="text-sm lg:text-base leading-[1.85] text-[#0A0A0A]/70 font-light">
                {heroSubtitle}
              </p>
              {heroFooter && (
                <div className="mt-8 pt-6 border-t border-[#0A0A0A]/10 text-[0.6rem] tracking-[0.4em] uppercase text-[#0A0A0A]/45">
                  {heroFooter}
                </div>
              )}
            </div>
          </div>

          {/* Secondary CTA — always visible so clients know the menu exists.
              Disabled state when no PDF is uploaded yet in the Dashboard. */}
          {menuInfo?.available ? (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL || ""}${menuInfo.url}`}
              target="_blank"
              rel="noopener noreferrer"
              download={menuInfo.filename || "menu-le-kaai.pdf"}
              onClick={() => trackEvent("lekaai_menu_open", {})}
              className="mt-2 inline-flex items-center gap-3 bg-white/12 hover:bg-white text-white hover:text-[#0A0A0A] backdrop-blur border border-white/40 px-8 py-3.5 text-[0.7rem] tracking-[0.35em] uppercase transition-all"
              data-testid="lekaai-menu-cta"
            >
              Consulter notre menu
              <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          ) : (
            <div
              className="mt-2 inline-flex items-center gap-3 bg-white/8 border border-white/25 text-white/60 px-8 py-3.5 text-[0.7rem] tracking-[0.35em] uppercase cursor-not-allowed"
              title="Le menu sera bientôt disponible"
              data-testid="lekaai-menu-cta-disabled"
            >
              Menu à venir
            </div>
          )}
        </div>
      </section>

      {/* ─── Reservation form — revealed after a date is picked ─── */}
      {selected && (
        <section
          ref={formRef}
          className="py-20 md:py-28 bg-white scroll-mt-24"
          data-testid="lekaai-form-section"
        >
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-10 md:mb-14">
              <div className="text-[0.6rem] tracking-[0.45em] uppercase text-[#B8922A] mb-4">
                · Étape 2 · Vos informations ·
              </div>
              <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight mb-4">
                Restaurant Le Kaai
              </h2>
              <p className="text-sm text-[#0A0A0A]/60 mt-2 capitalize">{weekdayLabel}</p>
              <p className="text-sm md:text-base text-[#0A0A0A]/70 font-light leading-relaxed mt-6 max-w-xl mx-auto">
                Le repas est réglé sur place. La réservation en ligne couvre
                uniquement la traversée aller-retour vers l&apos;île — {fmtXOF(CROSSING_FEE_PER_PERSON)} par personne.
              </p>
            </div>

            {/* Form */}
            <div className="bg-[#FAF7F2] p-6 sm:p-10 md:p-12 border border-[#0A0A0A]/8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Nom *" value={form.surname} onChange={(v) => setForm({ ...form, surname: v })} testId="lekaai-surname" />
                <Field label="Prénom *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="lekaai-name" />
                <Field label="Adresse e-mail *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="lekaai-email" />
                <Field label="Téléphone *" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="lekaai-phone" />
                <Counter label="Nombre de personnes *" value={form.guests} onChange={(v) => setForm({ ...form, guests: v })} min={1} max={40} testId="lekaai-guests" />
                <Select label="Heure au restaurant *" value={form.restaurant_time} onChange={(v) => setForm({ ...form, restaurant_time: v })} options={RESTAURANT_TIMES} testId="lekaai-resto-time" />
                <div className="md:col-span-2">
                  <Select label="Heure de départ du bateau *" value={form.boat_time} onChange={(v) => setForm({ ...form, boat_time: v })} options={boatOptions} testId="lekaai-boat-time" />
                </div>
              </div>

              {/* Total recap */}
              <div className="mt-8 pt-6 border-t border-[#0A0A0A]/10 flex items-baseline justify-between">
                <div>
                  <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#0A0A0A]/50">Traversée aller-retour</div>
                  <div className="text-xs text-[#0A0A0A]/55 mt-1">{form.guests} pers. × {fmtXOF(CROSSING_FEE_PER_PERSON)}</div>
                </div>
                <div className="font-serif text-3xl text-[#B8922A]" data-testid="lekaai-total">
                  {fmtXOF(totalAmount)}
                </div>
              </div>

              {error && (
                <div className="mt-5 text-sm text-[#C24226] bg-[#C24226]/5 px-4 py-2.5" data-testid="lekaai-error">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-[#0A0A0A] hover:bg-[#B8922A] text-white py-4 text-[0.7rem] tracking-[0.35em] uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="lekaai-submit"
              >
                {submitting ? (
                  <><Loader2 size={14} className="animate-spin" strokeWidth={1.5} /> Envoi…</>
                ) : (
                  <>Régler la traversée <ArrowRight size={13} strokeWidth={1.5} /></>
                )}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-[0.6rem] tracking-[0.35em] uppercase text-[#0A0A0A]/55 block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#0A0A0A]/15 focus:border-[#B8922A] outline-none px-4 py-3 text-sm bg-white transition-colors"
        data-testid={testId}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[0.6rem] tracking-[0.35em] uppercase text-[#0A0A0A]/55 block mb-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#0A0A0A]/15 focus:border-[#B8922A] outline-none px-4 py-3 text-sm bg-white transition-colors"
        data-testid={testId}
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Counter({ label, value, onChange, min = 1, max = 40, testId }) {
  return (
    <div>
      <label className="text-[0.6rem] tracking-[0.35em] uppercase text-[#0A0A0A]/55 block mb-2">{label}</label>
      <div className="flex items-center justify-between gap-2 border border-[#0A0A0A]/15 px-2 py-1.5 bg-white">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))}
                className="w-9 h-9 hover:bg-[#0A0A0A]/5 text-lg leading-none text-[#0A0A0A]/70 transition-colors"
                data-testid={`${testId}-minus`}>−</button>
        <div className="text-lg font-serif font-light text-[#0A0A0A]" data-testid={`${testId}-value`}>{value}</div>
        <button type="button" onClick={() => onChange(Math.min(max, value + 1))}
                className="w-9 h-9 hover:bg-[#0A0A0A]/5 text-lg leading-none text-[#0A0A0A]/70 transition-colors"
                data-testid={`${testId}-plus`}>+</button>
      </div>
    </div>
  );
}
